import { useEffect, useRef, useState } from "hono/jsx";

interface MenuProps {
  isHost?: boolean;
  isPublic?: boolean;
  currentExperiment?: string;
  chunkDepth?: number;
  chunkSize?: number;
  showGrid?: boolean;
  smartRender?: boolean;
  meshRender?: boolean;
}

export function Menu(
  {
    isHost = false,
    isPublic = false,
    currentExperiment = "",
    chunkDepth = 1,
    chunkSize = 16,
    showGrid = true,
    smartRender = false,
    meshRender = false,
  }: MenuProps,
) {
  const menuRef = useRef<HTMLDialogElement>(null);

  // Local state for controlled components and internal logic
  const [localCurrentExperiment, setLocalCurrentExperiment] = useState(
    currentExperiment,
  );
  const [localChunkDepth, setLocalChunkDepth] = useState(chunkDepth);
  const [localChunkSize, setLocalChunkSize] = useState(chunkSize);
  const [localViewDistance, setLocalViewDistance] = useState(2); // Assuming a default for view distance
  const [localShowGrid, setLocalShowGrid] = useState(showGrid);
  const [localSmartRender, setLocalSmartRender] = useState(smartRender);
  const [localMeshRender, setLocalMeshRender] = useState(meshRender);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (e.repeat) return; // Ignore hold
        if (e.defaultPrevented) return; // Handled by another listener
        if (!menuRef.current) return; // Component unmounted/ref stale

        e.preventDefault(); // Prevent native dialog close to manual toggle

        if (menuRef.current?.open) {
          menuRef.current?.close();
          document.dispatchEvent(new CustomEvent("pg:menu:closed"));
        } else {
          menuRef.current?.showModal();
          document.dispatchEvent(new CustomEvent("pg:menu:opened"));
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Effect to update local state when props change (e.g., from server updates)
  // AND listen to custom events from playground.ts
  useEffect(() => {
    setLocalCurrentExperiment(currentExperiment);
    setLocalChunkDepth(chunkDepth);
    setLocalChunkSize(chunkSize);
    setLocalShowGrid(showGrid);
    setLocalSmartRender(smartRender);
    setLocalMeshRender(meshRender);

    const onStateUpdate = (e: any) => {
      const detail = e.detail;
      if (detail.chunkDepth !== undefined) {
        setLocalChunkDepth(detail.chunkDepth);
      }
      if (detail.chunkSize !== undefined) setLocalChunkSize(detail.chunkSize);
      if (detail.showGrid !== undefined) setLocalShowGrid(detail.showGrid);
      if (detail.smartRender !== undefined) {
        setLocalSmartRender(detail.smartRender);
      }
      if (detail.meshRender !== undefined) {
        setLocalMeshRender(detail.meshRender);
      }
      if (detail.experiment !== undefined) {
        setLocalCurrentExperiment(detail.experiment); // If sent (usually INIT)
      }
    };

    document.addEventListener("pg:state:update", onStateUpdate);

    return () => {
      document.removeEventListener("pg:state:update", onStateUpdate);
    };
  }, [
    currentExperiment,
    chunkDepth,
    chunkSize,
    showGrid,
    smartRender,
    meshRender,
  ]);

  const switchExperiment = (exp: string) => {
    setLocalCurrentExperiment(exp); // Update local state
    document.dispatchEvent(
      new CustomEvent("pg:switch:experiment", { detail: exp }),
    );
    menuRef.current?.close();
    document.dispatchEvent(new CustomEvent("pg:menu:closed"));
  };

  const clearChunks = () => {
    document.dispatchEvent(new CustomEvent("pg:clear:chunks"));
  };

  const togglePublic = () => {
    document.dispatchEvent(
      new CustomEvent("pg:set:public", { detail: !isPublic }),
    );
  };

  const setDepth = (depth: number) => {
    setLocalChunkDepth(depth); // Update local state
    document.dispatchEvent(new CustomEvent("pg:set:depth", { detail: depth }));
  };

  const setSize = (size: number) => {
    setLocalChunkSize(size);
    document.dispatchEvent(
      new CustomEvent("pg:set:chunksize", { detail: size }),
    );
  };

  const setViewDistance = (distance: number) => { // Added setViewDistance handler
    setLocalViewDistance(distance); // Update local state
    document.dispatchEvent(
      new CustomEvent("pg:set:viewdistance", { detail: distance }),
    );
  };

  const toggleGrid = () => {
    const newShowGrid = !localShowGrid;
    setLocalShowGrid(newShowGrid); // Update local state
    document.dispatchEvent(
      new CustomEvent("pg:set:grid", { detail: newShowGrid }),
    );
  };

  const toggleSmartRender = () => {
    const newSmartRender = !localSmartRender;
    setLocalSmartRender(newSmartRender); // Update local state
    document.dispatchEvent(
      new CustomEvent("pg:set:smartrender", { detail: newSmartRender }),
    );
  };

  const toggleMeshRender = () => {
    const newMeshRender = !localMeshRender;
    setLocalMeshRender(newMeshRender); // Update local state
    document.dispatchEvent(
      new CustomEvent("pg:set:meshrender", { detail: newMeshRender }),
    );
  };

  return (
    <dialog
      ref={menuRef}
      className="modal bg-base-300/95 backdrop:bg-black/50"
      onClose={() => document.dispatchEvent(new CustomEvent("pg:menu:closed"))}
    >
      <div className="modal-box text-center">
        <h2 className="text-2xl font-bold mb-6">Menu</h2>

        {isHost
          ? (
            <div className="space-y-4">
              <p className="text-sm opacity-70">Experiments</p>
              <div className="form-control w-full max-w-xs mx-auto">
                <select
                  className="select select-bordered select-sm w-full"
                  value={localCurrentExperiment}
                  onChange={(e) =>
                    switchExperiment((e.target as HTMLSelectElement).value)}
                >
                  <option value="" disabled>Select Experiment</option>
                  <option value="chunks">Chunks</option>
                  <option value="chunks_culled">Chunks + Cull</option>
                  <option value="walk">Walk</option>
                  <option value="cubes">Cubes</option>
                  <option value="cubes_persistent">Cubes (Persistent)</option>
                </select>
              </div>

              {(localCurrentExperiment === "cubes" ||
                localCurrentExperiment === "cubes_persistent") && (
                <>
                  <div className="form-control w-full max-w-xs mx-auto mt-2">
                    <label className="label">
                      <span className="label-text">Chunk Depth</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="500"
                      className="input input-bordered input-sm w-full"
                      value={localChunkDepth}
                      onChange={(e) =>
                        setDepth(
                          parseInt((e.target as HTMLInputElement).value) || 1,
                        )}
                    />
                  </div>

                  <div className="form-control w-full max-w-xs mx-auto mt-2">
                    <label className="label">
                      <span className="label-text">Chunk Size</span>
                    </label>
                    <input
                      type="range"
                      min="8"
                      max="64"
                      step="8"
                      className="range range-xs"
                      value={localChunkSize}
                      onChange={(e) =>
                        setSize(
                          parseInt((e.target as HTMLInputElement).value) || 16,
                        )}
                    />
                    <div className="text-xs text-center mt-1">
                      {localChunkSize}
                    </div>
                  </div>

                  <div className="form-control w-full max-w-xs mx-auto mt-2">
                    <label className="label">
                      <span className="label-text">View Distance</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="16"
                      className="input input-bordered input-sm w-full"
                      value={localViewDistance}
                      onChange={(e) =>
                        setViewDistance(
                          parseInt((e.target as HTMLInputElement).value) || 1,
                        )}
                    />
                  </div>
                </>
              )}

              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Show Grid</span>
                  <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={showGrid}
                    onChange={toggleGrid}
                  />
                </label>
              </div>

              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Smart Render</span>
                  <input
                    type="checkbox"
                    className="toggle toggle-secondary"
                    checked={smartRender}
                    onChange={toggleSmartRender}
                  />
                </label>
              </div>

              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Mesh Render</span>
                  <input
                    type="checkbox"
                    className="toggle toggle-accent"
                    checked={meshRender}
                    onChange={toggleMeshRender}
                  />
                </label>
              </div>

              <div className="divider"></div>

              <button
                type="button"
                className="btn btn-warning btn-sm"
                onClick={clearChunks}
              >
                Clear Chunks
              </button>

              <div className="divider"></div>

              <label className="label cursor-pointer justify-center gap-4">
                <span className="label-text">Public Room</span>
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={isPublic}
                  onChange={togglePublic}
                />
              </label>
            </div>
          )
          : (
            <div className="py-4 opacity-50 italic">
              Asking host to change experiments...
            </div>
          )}

        <div className="modal-action justify-center">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              menuRef.current?.close();
              document.dispatchEvent(new CustomEvent("pg:menu:closed"));
            }}
          >
            Resume
          </button>
        </div>

        <p className="text-xs opacity-50 mt-4">[ESC] to close</p>
      </div>
    </dialog>
  );
}
